import type { SetStoreFunction } from 'solid-js/store'
import { IObjectImpl, ObjectImpl, ObjectType, ResizeHandles, type GeometrySeed, type HugFit, Dimensions } from '../interactions/ObjectImpl'
import type { DragContext } from '../drag'
import type { SelectionContext } from '../selection'
import { textRendersEmpty } from '../text/BaseTextObject'
import { clampAxisIntoParent } from '../interactions/dragBounds'
import { extractCorners, extractShapeAttrs, type Corners, type ShapeAttrs } from '../styleAttrs'
import { resolveEffectiveStyle, type ThemeRecord } from '../../project/themeVariables'
import type { Style, ContainerLayout, Canvas } from '../../project/posterSchema'
import type { RuntimeContainer, RuntimeObject, DataRecord, Box, Position } from '../../types'

// ── Flex-column layout ────────────────────────────────────────────────────────
//
// SVG <g> elements can't use CSS flexbox, so a container's "flex column" layout is computed here
// in JS from each child's measured size. Returns each child's absolute {left, top} within the
// container; the values are handed to the child as a layoutPos override (see IRenderContext.
// layoutPos). Vertical placement follows the matching CSS justify-content value; horizontal
// follows `align` (align-items).

// coy = the child's contentOffsetY: text renders its glyphs above the box origin, so the visible
// top is at boxTop + coy. The column is laid out in visible space (using h), then each box origin
// is set to visibleTop − coy so the content lands as intended.
export interface ChildBox { id: string; dims: Dimensions; coy: number }

// Backstop against a self-referential layout graph: the set of container ids whose min-size is
// currently being computed. The measurement seams (computeMinSize below) are cycle-free by
// construction, so this should never fire — but if a container ever ends up measuring itself
// (e.g. a corrupt/aliased object tree), computeMinSize returns the floor instead of recursing,
// degrading to a cosmetic glitch rather than overflowing the stack and freezing the tab.
const __measuring = new Set<string>()

// Cross-axis (horizontal) box-origin left for a child of width w in a container of width W.
function alignLeft(align: 'left' | 'center' | 'right', W: number, w: number): number {
	return align === 'center' ? (W - w) / 2 : align === 'right' ? W - w : 0
}

// A measurement crossing into the container's math, with anything non-finite replaced by `fallback`.
// A NaN never stays local here: the column sums its children's heights, so one NaN makes every
// sibling's top NaN too, and the invalid transform that produces stacks the whole column at the
// container's origin. A bad measurement should cost that one child its size, not the layout. The
// non-numeric style value that causes this is already refused upstream (see resolveEffectiveStyle's
// NUMERIC_STYLE_KEYS); this keeps any future source of one contained.
function finiteOr(v: number | undefined, fallback: number): number {
	return Number.isFinite(v) ? v as number : fallback
}

export function computeFlexColumn(
	mode:  Exclude<ContainerLayout, 'absolute'>,
	gap:   number,
	align: 'left' | 'center' | 'right',
	W:     number,
	H:     number,
	boxes: ChildBox[],
): Record<string, Position> {
	const n      = boxes.length
	const totalH = boxes.reduce((sum, b) => sum + b.dims.height, 0)
	const tops:  number[] = []

	if (mode === 'top' || mode === 'center' || mode === 'bottom') {
		const packed = totalH + gap * Math.max(0, n - 1)
		let y = mode === 'top' ? 0 : mode === 'center' ? (H - packed) / 2 : H - packed
		for (const b of boxes) { tops.push(y); y += b.dims.height + gap }
	} else {
		// Clamp to non-negative: when the children overflow the container, the distribution modes
		// otherwise hand out negative space. 'between' pins its endpoints so it only overlaps
		// internally, but 'around'/'evenly' pad the ends, so negative end-padding pushes the first
		// child above the top edge and the last below the bottom. Collapsing spacing to 0 makes all
		// three degrade the same way: stack from the top and overflow only downward.
		const free    = Math.max(0, H - totalH)
		const spacing = mode === 'between' ? (n > 1 ? free / (n - 1) : 0)
			: mode === 'around' ? free / n
			:                     free / (n + 1)   // evenly
		let y = mode === 'around' ? spacing / 2 : mode === 'evenly' ? spacing : 0
		for (const b of boxes) { tops.push(y); y += b.dims.height + spacing }
	}

	const result: Record<string, Position> = {}
	boxes.forEach((b, i) => {
		result[b.id] = { x: alignLeft(align, W, b.dims.width), y: tops[i] - b.coy }
	})
	return result
}

// ── Auto-resize anchoring ──────────────────────────────────────────────────────
//
// When a container grows to fit its contents (newH > storedH) these decide where the taller box
// sits. Direction follows the layout: top-aligned keeps its top (grows down), bottom-aligned keeps
// its bottom (grows up), everything else keeps its centre (grows 50% each way). Mirrors
// TemplateTextImpl.anchorTop.
export function idealContainerTop(mode: ContainerLayout, storedTop: number, storedH: number, newH: number): number
{
	if (mode === 'top')    return storedTop
	if (mode === 'bottom') return storedTop + storedH - newH
	return storedTop + (storedH - newH) / 2   // center / around / evenly / between / absolute
}

// The ideal top clamped into the wrapping container / canvas, so an expansion that would overflow
// one side is redistributed to the other (e.g. only 20% room below → 20% down, 80% up). When the
// content is taller than the wrapper (room < 0) the box overflows symmetrically rather than pinning.
export function placedContainerTop(mode: ContainerLayout, parentH: number, storedTop: number, storedH: number, newH: number): number
{
	const ideal = idealContainerTop(mode, storedTop, storedH, newH)
	return clampAxisIntoParent(ideal, newH, parentH)
}

const FLOOR_W = 30, FLOOR_H = 20

// ── Reduce-to-fit when a child is added to a container ──────────────────────────
//
// A newly added child is reduced from its default size so it fits the space the container has for
// it — but only on the axis that is actually constrained, and only to the minimum extent required
// (never below a small "addition" floor). If even the floor doesn't fit horizontally, the caller
// widens the container (placedContainerLeft); vertical shortfall is absorbed by the container's own
// reactive auto-grow. These are pure: the caller measures the container and its children and passes
// plain numbers.

// Default add sizes for the box-sized types. Text is content-hugging (no stored box) and its font is
// never shrunk, so it isn't listed here — see the text branch of fitAddedChild.
export const ADD_DEFAULTS   = { image: [160, 120], container: [200, 100] } as const
export const ADD_MIN_WH     = 30   // px floor per axis for images / containers
export const DEFAULT_FONT   = 24
export const ADD_TEXT_DEF_W = 200  // default reserved width of a field text
// A text object keeps its default font (we never shrink it); its minimum addition size is one line
// tall × ~5 characters wide. No DOM at add time, so char width is estimated as a fraction of the em.
export const ADD_TEXT_MIN_CHARS = 5
export const CHAR_W_RATIO       = 0.6

export interface AddFit {
	size?:     { width?: number; height?: number } // stored size to write (image / container / field text)
	contentW:  number   // the minimum box width the container must be able to hold
	needGrowW: boolean  // the floored width didn't fit availW → container must widen to admit it
}

// The reserved width a field text takes in a box `availW` wide: its designed width, reduced to fit
// but never below the addition minimum. Shared by the add path below and the plain → template source
// switch (which has to invent a reserve, since plain text hugs its ink and stores no width).
export function fieldReserveWidth(designW: number, availW: number, fontSize: number): number
{
	const minW = Math.ceil(ADD_TEXT_MIN_CHARS * fontSize * CHAR_W_RATIO)
	return Math.max(minW, Math.min(designW, availW))
}

// Reduce each axis independently to fit, floored at the addition minimum. Width and height are
// clamped separately, so a purely vertical (or horizontal) constraint leaves the other axis at its
// default. Text keeps its default font: plain text stays content-hugging (no size written) and only
// a field's reserved width is reduced; the returned contentW is the text's minimum admissible width.
export function fitAddedChild(
	type: 'text' | 'image' | 'container', isField: boolean,
	availW: number, availH: number, fontSize = DEFAULT_FONT,
): AddFit
{
	if (type === 'text') {
		const minW = Math.ceil(ADD_TEXT_MIN_CHARS * fontSize * CHAR_W_RATIO)
		// A field reserves a width, reduced to fit but never below the minimum; plain text has no box.
		const size = isField ? { width: fieldReserveWidth(ADD_TEXT_DEF_W, availW, fontSize) } : undefined
		return { size, contentW: minW, needGrowW: minW > availW }
	}

	const [w0, h0] = ADD_DEFAULTS[type]
	const width  = Math.max(ADD_MIN_WH, Math.min(w0, availW))
	const height = Math.max(ADD_MIN_WH, Math.min(h0, availH))
	return { size: { width, height }, contentW: width, needGrowW: width > availW }
}

// Horizontal analog of placedContainerTop. Width has no layout-direction concept, so anchor the
// left edge and grow rightward, clamped into the parent via clampAxisIntoParent (redistributing to
// the left when the wider box would overflow the right edge).
export function placedContainerLeft(parentW: number, storedLeft: number, storedW: number, newW: number): number
{
	return newW > storedW ? clampAxisIntoParent(storedLeft, newW, parentW) : storedLeft
}

// The container object's host configuration, shape styling, and flex-layout engine. The component
// instantiates this during setup, spreads the host members onto <DraggableElement>, and renders
// the background shape + recursive children in the render-prop, reading shapeAttrs()/isCircle()/
// visibleObjects()/childPositions()/nestedParentOffset.
export class ContainerImpl extends ObjectImpl implements IObjectImpl
{
	hasBg     = false;
	hasBorder = false;

	readonly id: string

	constructor(
		// The live-geometry store, built once in the component's stable owner and injected — so
		// re-running impl() (a derived read — see ContainerObject.tsx) never re-seeds the store.
		box:    Box,
		setBox: SetStoreFunction<Box>,
		seed:   GeometrySeed,
		private object:  RuntimeContainer,
		private canvas:  Canvas,
		// A plain value, not an accessor: like object, it's read fresh every time ContainerObject
		// rebuilds impl() (see ContainerObject.tsx) — effStyle()/visibleObjects() (plain derived
		// methods, not memos) read it directly and recompute on every call, so a field-bound shape
		// style or a record-dependent empty child re-resolves without ever going stale.
		private record:  DataRecord,
		// Resolved theme variables — read by effStyle() to fold theme-bound style keys in.
		private theme:   ThemeRecord | undefined,
		private parentOffset: (() => Position) | undefined,
		// The container's background <rect>/<circle> (or <path>, when its corners carry different
		// radii), exposed to the registry via getEl() (read during export). A mutable ref box because
		// the element is set during render.
		private bgRef:   { current: SVGElement | null } | undefined,
		// Whether we're in a user-facing (preview) context. Resolved from ModeProvider by the component
		// (isUserFacing(mode)) and handed in as a plain accessor so the impl needn't hold the mode
		// context — read by visibleObjects()/effectiveBox()/computeMinSize().
		private useUser: () => boolean,
		// Live child-impl lookup, derived from the element registry by the component. The container reads
		// its children's measured sizes through this in its geometry methods; those run polymorphically
		// with no args (a parent measures a child container via the bare IObjectImpl.getSize()), so the
		// lookup is held here rather than passed to each call.
		private getElement: (id: string) => IObjectImpl | undefined,
		// Re-entrancy guard for computeChildPositions(). A flex child's getSize() can read its own
		// layoutPos accessor (see TemplateTextImpl.liveHugBox) to tell hugged content from a flex-managed
		// box, and that accessor calls straight back into this container's childPositions() — which used
		// to be a createMemo, so a reentrant read during its own computation resolved to the in-flight
		// (undefined) value instead of recursing. Now that childPositions is a plain recomputing method (a
		// fresh ContainerImpl per impl() call, so no instance state survives the reentrant call), this
		// flag reproduces that same short-circuit explicitly. Built once in the component's stable owner
		// rather than on ContainerImpl itself, since it must survive across the reentrant impl() call.
		private computingPositions: { current: boolean },
	)
	{
		super(box, setBox, seed)
		this.id        = object.id
		this.hasBg     = true
		this.hasBorder = true
	}

	// effStyle/shapeAttrs/visibleObjects/anyChildSelected/childPositions are plain methods, not
	// createMemo — they read this.record/this.object directly and recompute on every call, so a plain
	// (non-accessor) record is always current. No persistent state to hoist: unlike the text/image
	// impls, a container carries no signals of its own beyond pos/setPos/seed.
	private effStyle(): Style | undefined { return resolveEffectiveStyle(this.object, this.theme) }

	shapeAttrs(): ShapeAttrs { return extractShapeAttrs(this.effStyle()) }

	corners(): Corners { return extractCorners(this.effStyle()) }

	// In a user-facing context, a text box that renders to only whitespace is dropped from the
	// canvas entirely so the flex layout reclaims its slot. Design mode keeps every child so the
	// layout the designer sees is stable.
	visibleObjects(): RuntimeObject[]
	{
		const obj = this.object
		const objs = obj.objects ?? []
		return this.useUser() ? objs.filter(o => !textRendersEmpty(o, this.record)) : objs
	}

	// The mirror of ChildOutlines: when one of this container's own immediate children is
	// selected (and the container itself is not), give the container's border the same faint
	// editing-aid outline. A selected grandchild belongs to a nested container, which draws its
	// own outline, so it doesn't trigger this one.
	anyChildSelected(selCtx: SelectionContext): boolean
	{
		const obj = this.object
		return !selCtx.selectedIds().has(obj.id) && this.visibleObjects().some(o => selCtx.selectedIds().has(o.id))
	}

	childPositions(dragCtx: DragContext): Record<string, Position> { return this.computeChildPositions(dragCtx) }

	// The slot a reentrant read resolves to while computeChildPositions is on the stack (the component
	// routes past its memo for it — see isComputingPositions). Its coordinates are the child's stored
	// position: nothing reads them in that window (a reentrant probe only tests whether layoutPos()
	// returns anything at all), but the accessor still owes its caller a whole Position.
	inFlightChildPosition(child: RuntimeObject): Position
	{
		return child.position ?? { x: 0, y: 0 }
	}

	// True while computeChildPositions is on the stack. The component memoises childPositions() but must
	// route a flex child's reentrant self-read (its getSize() → own layoutPos accessor) past that memo —
	// reading it mid-computation resolves to the in-flight value. When this is set the accessor returns
	// the same truthy placeholder computeChildPositions itself hands back on reentry (see its guard).
	isComputingPositions(): boolean { return this.computingPositions.current }

	type() { return 'container' as ObjectType; }

	getEl(): SVGElement | null { return this.bgRef?.current ?? null }

	isField() { return false; }

	getHasFill():   boolean { return !this.isFillNone(this.shapeAttrs().fill); }
	getHasBorder(): boolean { return !!this.shapeAttrs().stroke; }
	getHugFit(): HugFit { return { box: { pos: this.box.pos, dims: this.computeMinSize() } } }

	// ── Auto-resize to fit contents ─────────────────────────────────────────────────
	//
	// The container's drawn height is derived, not the raw stored size: it grows to fit its
	// children and never shrinks below the designed height. The rule is identical in design mode
	// (auto-grow when too small) and user mode (the design height is a MINIMUM; template text can
	// render larger, so grow above it). Because computeMinSize() reads children's live sizes, this
	// re-derives reactively whenever any descendant changes size — no imperative fit pass.
	private effectiveHeight(): number
	{
		const content = this.computeMinSize().height
		const min     = this.box.dims.height ?? content
		return Math.max(min, content)
	}

	// The resolved render box, overriding the stored height with the fitted height and anchoring
	// the top per layout direction. Re-anchoring/clamping only kicks in when the box actually grows
	// beyond its designed height — a container that already fits keeps the designer's exact top
	// (including deliberately off-canvas placements). When this container is itself a flex child of
	// another (layoutPos supplies a top), the parent owns its position — keep that slot and don't
	// self-anchor; the parent grows to fit via our getSize() below. canvas.height is the wrapping
	// element's rendered height (ContainerObject passes its own size down as each child's canvas) or
	// the poster canvas at the top level.
	effectiveBox(userExists:boolean): Box
	{
		const base    = super.effectiveBox(userExists);
		const newH    = this.effectiveHeight()
		const storedH = this.box.dims.height ?? newH
		const managed = this.layoutPos?.() != null
		// Flex-managed: the parent container owns our position (and keeps us inside itself), so keep
		// the slot as computed and don't self-anchor or clamp.
		if (managed)
			return { pos: base.pos, dims: { width: base.dims.width, height: newH } }

		const mode = this.object.layout ?? 'absolute'
		// Grow-anchor: when the container grows past its designed height, re-anchor/clamp the top per
		// layout direction — design and user mode alike.
		let top  = newH > storedH
			? placedContainerTop(mode, this.canvas.height ?? newH, base.pos.y, storedH, newH)
			: base.pos.y
		let left = base.pos.x

		// User mode: pull the whole box back inside the parent (nearest container / canvas), so
		// resolved content that overflows the designed layout is nudged just inside its bounds. A
		// container carries no content offset. Derived only — the designed position is never persisted.
		if (this.useUser()) {
			const w = this.box.dims.width ?? 200
			left = clampAxisIntoParent(left, w,    this.canvas.width  ?? w)
			top  = clampAxisIntoParent(top,  newH, this.canvas.height ?? newH)
		}
		return { pos: { x: left, y: top }, dims: { width: base.dims.width, height: newH } }
	}

	// The seam a parent container reads (via childBoxes → getSize) to measure this container as a
	// flex child, so our grow/shrink propagates up the tree. Reads no position, so it forms no
	// reactive cycle through layoutPos/childPositions (see base ObjectImpl.getSize).
	getSize(): Dimensions 
	{ 
		return { width: this.box.dims.width ?? 200, height: this.effectiveHeight() };
	}

	// ── Inert host surface ─────────────────────────────────────────────────────────
	// A container has no box-width/field/text wiring; these satisfy the abstract ObjectImpl
	// surface with the same inert results the host already branches on.
	fieldHeight() { return undefined; }

	resizeHandles(): ResizeHandles { return 'box'; }

	lockAspect() { return true; }

	nestedParentOffset(userExists:boolean): Position
	{
	const obj  = this.object
		const po   = this.parentOffset?.() ?? { x: 0, y: 0 }
		const live = this.getElement(obj.id)?.getBBox?.(userExists)
		const p    = obj.position
		return {
			x: po.x + (live?.pos.x ?? p?.x ?? 0),
			y: po.y + (live?.pos.y ?? p?.y ?? 0),
		}
	}

	isCircle(): boolean { return String(this.effStyle()?.borderRadius) === '50%' }

	private isFillNone(v: string | undefined): boolean { return !v || v === 'none' || v === 'transparent' }

	// Minimum size the container may be resized to: large enough to still contain its children.
	// Absolute children keep their stored positions, so the floor is the furthest child edge. Flex
	// children can't compress, so the floor is the stacked content height (plus gaps for the
	// stacking modes) and the widest child's width.
	private computeMinSize(): Dimensions
	{
		const id = this.object.id
		if (__measuring.has(id)) return { width: FLOOR_W, height: FLOOR_H }
		__measuring.add(id)
		try {
			const obj   = this.object
			const mode  = obj.layout ?? 'absolute'
			const items = this.visibleObjects()
			if (items.length === 0) return { width: FLOOR_W, height: FLOOR_H }

			if (mode === 'absolute') {
				// Measure each child by its hit box — text renders its glyphs above the box origin (a
				// negative contentOffsetY), so the visible top sits at storedTop + coy; images and
				// containers have coy 0, so their hit box is the stored box. The floor is the furthest
				// child edge. Measured from the child's stored position + getSize() + getContentOffsetY()
				// — the same cycle-free seams the flex branch and containerSize() use — NOT getHitBBox(),
				// which routes through the child's effectiveBox → layoutPos and climbs back into this
				// container's own layout (infinite recursion; see containerSize's note).
				let w = FLOOR_W, h = FLOOR_H
				for (const o of items) {
					const el   = this.getElement(o.id)
					const size = el?.getSize?.()
					if (!size) continue
					const coy  = finiteOr(el?.getContentOffsetY?.(), 0)
					const p    = (o as { position?: { x?: number; y?: number } }).position ?? {}
					const left = finiteOr(p.x, 0)
					const top  = finiteOr(p.y, 0) + coy
					w = Math.max(w, left + finiteOr(size.width,  0))
					h = Math.max(h, top  + finiteOr(size.height, 0))
				}
				return { width: w, height: h }
			}

			const boxes   = this.childBoxes()
			const gap     = obj.gap ?? 10
			const stacked = mode === 'top' || mode === 'center' || mode === 'bottom'
			const totalH  = boxes.reduce((sum, b) => sum + b.dims.height, 0) + (stacked ? gap * Math.max(0, boxes.length - 1) : 0)
			const maxW    = boxes.reduce((m, b) => Math.max(m, b.dims.width), 0)
			return { width: Math.max(FLOOR_W, maxW), height: Math.max(FLOOR_H, totalH) }
		} finally { __measuring.delete(id) }
	}

	// The box the flex layout is placed inside. Height is the derived (fitted) height, so
	// center/bottom/distribution modes position children within the height the box is actually
	// drawn at — not the smaller stored height (which would let content overflow). Width still
	// comes from the stored/live size (auto-resize is vertical only). Measured via getSize (not
	// getBBox): getBBox reads each child's layoutPos → childPositions, which would make this
	// recompute its own input (infinite recursion).
	private containerSize(): Dimensions
	{
		const obj    = this.object
		const canvas = this.canvas
		const live   = this.getElement(obj.id)?.getSize?.()
		return {
			width:  live?.width ?? obj.size?.width ?? canvas.width,
			height: this.effectiveHeight(),
		}
	}

	private childBoxes(): ChildBox[]
	{
		return this.visibleObjects().map(o => {
			const el    = this.getElement(o.id)
			const live  = el?.getSize?.()
			const sized = o as { size?: { width?: number; height?: number } }
			return {
				id:  o.id,
				dims: {
					width:  finiteOr(live?.width  ?? sized.size?.width,  0),
					height: finiteOr(live?.height ?? sized.size?.height, 0),
				},
				coy: finiteOr(el?.getContentOffsetY?.(), 0),
			}
		})
	}

	// Computed live in every mode from the children's measured sizes, so design and the user
	// preview use the identical code path and measurements — no baked snapshot to drift against.
	// (syncLivePositions still bakes positions at switch time, but the override re-derives them
	// here, so the two always agree.)
	private computeChildPositions(dragCtx: DragContext): Record<string, Position>
	{
		// See the computingPositions constructor param: a flex child's getSize() can call straight back
		// into this same computation via its own layoutPos accessor — routinely, on every measurement
		// pass, not as a rare edge case. That reentrant call only happens from within childBoxes() below,
		// which only runs once we're already past the absolute-mode early-return, so we're always inside
		// a genuine flex computation here. Returning an entry per child, rather than an empty map,
		// matters: liveHugBox()/getMinSize() branch on whether layoutPos() returns anything at all to
		// tell "I'm flex-managed" from "I'm free-floating" — a missing entry makes every lookup
		// undefined, so a reentrant probe from a text field's own getSize() would misread itself as NOT
		// flex-managed and hug its content instead of reporting its reserved field-width box, corrupting
		// the very box this computation is trying to measure.
		if (this.computingPositions.current)
			return Object.fromEntries((this.object.objects ?? []).map(o => [o.id, this.inFlightChildPosition(o)]))
		this.computingPositions.current = true
		try {
			const obj  = this.object
			const mode = obj.layout ?? 'absolute'
			if (mode === 'absolute') return {}
			const boxes = this.childBoxes()
			if (boxes.length === 0) return {}
			const { width: w, height: h } = this.containerSize()
			const align = obj.align ?? 'left'
			const positions = computeFlexColumn(mode, obj.gap ?? 10, align, w, h, boxes)
			// During a vertical drag-reorder the array is already reordered (so siblings reflow into
			// their new slots), but the dragged child floats at the cursor instead of snapping to its
			// slot. Override its top with the live drag position; its slot is left as the visible gap.
			const reorder = dragCtx?.reorderDrag() ?? false;
			if (reorder && positions[reorder.id]) positions[reorder.id] = { x: positions[reorder.id].x, y: reorder.top }
			return positions
		} finally {
			this.computingPositions.current = false
		}
	}
}
